import DocumentStore from 'notebook/documents/DocumentStore';
import {computeCellGridCost} from 'notebook/tables/excess/CostAnalyzer';
import Parser from 'notebook/tables/excess/Parser';
import {Table} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {AnyStructuredTable, Note, NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';

export interface TablePerformanceSummary {
  tableId: string;
  documentName: string | null;
  name: string;
  numCells: number;
  numRows: number;
  numColumns: number;
  formulaCost: number;
  costBreakdown: {[key: string]: number};
}

export interface NotePerformanceSummary {
  numDocuments: number;
  numTables: number;
  numCells: number;
  totalFormulaCost: number;
  tableSummaries: Array<TablePerformanceSummary>;
}

export type FormulaCostTier = 'over_limit' | 'nearing_limit' | 'good';
export const TOTAL_FORMULA_COST_LIMIT = 165_000;
export const TOTAL_FORMULA_COST_LIMIT_WARNING = TOTAL_FORMULA_COST_LIMIT * 0.5;

export const TABLE_FORMULA_COST_LIMIT = 30_000;
export const TABLE_FORMULA_COST_LIMIT_WARNING = TABLE_FORMULA_COST_LIMIT * 0.5;

export const _forTesting = {
  getPlainTablePerformanceSummary,
  getStructuredTablePerformanceSummary,
};

function getPlainTablePerformanceSummary(
  table: Table,
  documentName: string | null,
  formulaCost: number,
  costBreakdown: {[key: string]: number}
): TablePerformanceSummary {
  const numRows = table.table.length;
  const numColumns = table.table[0].length;
  const numCells = numRows * numColumns;

  return {
    tableId: `${documentName}-${table.name}`,
    documentName,
    name: table.name,
    numCells,
    numRows,
    numColumns,
    formulaCost,
    costBreakdown,
  };
}

function getStructuredTablePerformanceSummary(
  table: AnyStructuredTable,
  documentName: string | null,
  formulaCost: number,
  costBreakdown: {[key: string]: number}
): TablePerformanceSummary {
  const numRows = table.rows.length;
  const numColumns = table.columns.length;
  const numCells = numRows * numColumns;
  return {
    tableId: `${documentName}-${table.name}`,
    documentName,
    name: table.name,
    numCells,
    numRows,
    numColumns,
    formulaCost,
    costBreakdown,
  };
}

function formulaCostIsOverMaximum(formulaCost: number): boolean {
  return formulaCost >= TOTAL_FORMULA_COST_LIMIT;
}

function formulaCostIsInWarningRange(formulaCost: number): boolean {
  return formulaCost >= TOTAL_FORMULA_COST_LIMIT_WARNING && formulaCost < TOTAL_FORMULA_COST_LIMIT;
}

function tableFormulaCostIsOverMaximum(formulaCost: number): boolean {
  return formulaCost >= TABLE_FORMULA_COST_LIMIT;
}

function tableFormulaCostIsOverWarningThreshold(formulaCost: number): boolean {
  return formulaCost >= TABLE_FORMULA_COST_LIMIT_WARNING;
}

export const TableLimitUtils = {
  formulaCostIsOverMaximum,
  formulaCostIsInWarningRange,

  getFormulaCostTier(totalFormulaCost: number): FormulaCostTier {
    return formulaCostIsOverMaximum(totalFormulaCost)
      ? 'over_limit'
      : formulaCostIsInWarningRange(totalFormulaCost)
        ? 'nearing_limit'
        : 'good';
  },

  getFormulaCostTierForTable(tableFormulaCost: number): FormulaCostTier {
    return tableFormulaCostIsOverMaximum(tableFormulaCost)
      ? 'over_limit'
      : tableFormulaCostIsOverWarningThreshold(tableFormulaCost)
        ? 'nearing_limit'
        : 'good';
  },

  analyzeNotePerformance(note: Note): NotePerformanceSummary {
    const tables = TableUtil.getTablesFromNote(note);
    return this.analyzePerformanceOfTables({entry: tables});
  },

  analyzePerformanceOfTables(tablesByDocumentName: {
    [documentName: string]: Array<Table | AnyStructuredTable>;
  }): NotePerformanceSummary {
    let numCells = 0;
    let numTables = 0;
    let totalFormulaCost = 0;
    const numDocuments = Object.keys(tablesByDocumentName).length;
    const tableSummaries = Object.entries(tablesByDocumentName).flatMap(([documentName, tables]) =>
      tables.flatMap((table) => {
        // eslint-disable-next-line benchlint/no-in-operator
        if ('type' in table && table.type === NoteLinkedObjectType.STRUCTURED_TABLE) {
          const cellGrid = table.rows.map((row) => row.cells);
          const {totalCost: formulaCost, costBreakdown} = computeCellGridCost(cellGrid, Parser.parse);
          return getStructuredTablePerformanceSummary(table, documentName, formulaCost, costBreakdown);
        }
        const plainTable = table as Table;
        const {totalCost: formulaCost, costBreakdown} = computeCellGridCost(plainTable.table, Parser.parse);
        return getPlainTablePerformanceSummary(plainTable, documentName, formulaCost, costBreakdown);
      })
    );

    for (const summary of tableSummaries) {
      numTables++;
      numCells += summary.numCells;
      totalFormulaCost += summary.formulaCost;
    }

    return {
      numDocuments,
      numTables,
      numCells,
      totalFormulaCost,
      tableSummaries,
    };
  },
};

export function getTablesByDocumentNameFromDocumentIds(documentIds: Array<string>): {
  [documentName: string]: Array<Table | AnyStructuredTable>;
} {
  const tablesByDocumentName: {[documentName: string]: Array<Table | AnyStructuredTable>} = {};
  for (const documentId of documentIds) {
    const document = DocumentStore.getDocument(documentId);
    if (document) {
      tablesByDocumentName[document.name] = TableUtil.getTablesFromNote(
        document.documentVersion.entryVersion.note
      );
    }
  }
  return tablesByDocumentName;
}
